import React, { createContext, type DependencyList, type FC, type PropsWithChildren, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react'
import { createStore, type StoreApi } from 'zustand/vanilla'
import { useStoreWithEqualityFn } from 'zustand/traditional'

import { withCaptured } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../../domain'

/**
 * A drop-in replacement for `react-extended-state` (1.0.11, github.com/filipomar/react-extended-state), built on a Zustand store.
 *
 * Why it replaces the library:
 * - it's unmaintained: last published Aug 2022, tested against React 17 only;
 * - it keeps each selection in `useState` and refreshes it from a layout effect, so a selector with `dependencies` returns the
 *   previous value in the render where they change, and it can tear under React 18's concurrent rendering.
 * Zustand's `useStoreWithEqualityFn` (`zustand/traditional`, on `useSyncExternalStore`) selects during render instead.
 *
 * The library's API and behaviour are kept, so a store moving over only changes its import:
 * - the names `createExtendedState` and `ExtendedStateManager`;
 * - both argument orders of `useExtendedState(selector, filter?, dependencies?)`, and both filter forms (equality and hash);
 * - the `Provider`'s `value` or `manager` prop, with `ignorePropsChanges`;
 * - the manager's `getState`/`setState`/`subscribe(filter?, subscriber)`, which `ApplicationStateService` relies on;
 * - a notification on every `setState`, and each subscriber's own `previous` state.
 * Two differences, on purpose: the selection is current in the render where `dependencies` change, and the dispatcher keeps its
 * identity across renders.
 *
 * The design and the comparison with the library: `lore/engineering/CC-4935-zustand/Zustand_Adapter.plan.md`.
 */

type PossibleExtendedState = Record<string | number | symbol, unknown>

/**
 * Tells whether two values are the same, either directly (`(a, b) => boolean`) or by comparing their hashes
 */
export type Filter<S> = ((result: S) => string | number | null | undefined) | ((a: S, b: S) => boolean)

export type Selector<S, R> = (s: S) => R

export type Dispatcher<S> = () => (newStateOrGenerator: Partial<S> | ((currentState: S) => Partial<S>)) => void

type ReadonlyStoreApi<S> = Pick<StoreApi<S>, 'getState' | 'getInitialState' | 'subscribe'>

const areResultsEqual = <S,>(previous: S, current: S, filter: Filter<S>): boolean => {
    const result = filter(previous, current)
    return typeof result === 'boolean' ? result : result === filter(current, previous)
}

export class ExtendedStateManager<S extends PossibleExtendedState> {
    private readonly store: StoreApi<S>

    constructor (state: S) {
        this.store = createStore<S>()(() => state)
    }

    /**
     * Read-only on purpose: writes go through `setState`, so a subclass can wrap every one of them
     */
    get api (): ReadonlyStoreApi<S> {
        return this.store
    }

    getState (): S {
        return this.store.getState()
    }

    /**
     * Always notifies, even when `state` is the current state object, which Zustand alone would skip
     */
    setState (state: Partial<S>): void {
        this.store.setState({ ...state })
    }

    subscribe (...args: [filter: Filter<S>, subscriber: VoidFunction] | [subscriber: VoidFunction]): VoidFunction {
        const [filter, subscriber] = args.length === 1 ? [() => false, args[0]] : args
        /**
         * Kept per subscriber rather than taken from Zustand's `previous`,
         * so a `setState` nested in a subscriber doesn't hand later subscribers an older pair
         */
        let previous = this.store.getState()
        return this.store.subscribe(() => {
            const current = this.store.getState()
            if (!areResultsEqual(previous, current, filter)) {
                subscriber()
            }
            previous = current
        })
    }
}

type ProviderProps<S extends PossibleExtendedState> = Readonly<{ value: S } | { manager: ExtendedStateManager<S> }>

type UseExtendedStateArgs<S, R> =
    | [selector: Selector<S, R>, filter?: Filter<R>, dependencies?: DependencyList]
    | [selector: Selector<S, R>, dependencies?: DependencyList, filter?: Filter<R>]

type ExtendedState<S extends PossibleExtendedState> = Readonly<{
    Provider: FC<PropsWithChildren<ProviderProps<S>>>
    /**
     * The selector and the filter are kept until `dependencies` change, so a render without a store change returns the same selection,
     * and `dependencies` must list what the selector closes over
     */
    useExtendedState: <R>(...args: UseExtendedStateArgs<S, R>) => R
    useExtendedStateDispatcher: Dispatcher<S>
}>

const selectState = <S extends PossibleExtendedState>(props: ProviderProps<S>): S => ('value' in props ? props.value : props.manager.getState())

export const createExtendedState = <S extends PossibleExtendedState>({
    ignorePropsChanges = false,
}: Readonly<{ ignorePropsChanges?: boolean }> = {}): ExtendedState<S> => {
    const Context = createContext<ExtendedStateManager<S> | null>(null)

    const useManager = (caller: Parameters<typeof withCaptured>[1]): ExtendedStateManager<S> => {
        const manager = useContext(Context)
        if (manager === null) {
            throw withCaptured(new ConnectwareError(ConnectwareErrorType.STATE, 'Extended state must be used within its Provider'), caller)
        }
        return manager
    }

    const Provider: FC<PropsWithChildren<ProviderProps<S>>> = ({ children, ...props }) => {
        const [manager] = useState(() => ('value' in props ? new ExtendedStateManager(props.value) : props.manager))

        useLayoutEffect(() => {
            const state = selectState(props)
            if (!ignorePropsChanges && state !== manager.getState()) {
                manager.setState(state)
            }
        }, ['value' in props ? props.value : props.manager])

        return <Context.Provider value={manager}>{children}</Context.Provider>
    }

    const useExtendedState = <R,>(...[selector, ...rest]: UseExtendedStateArgs<S, R>): R => {
        const manager = useManager(useExtendedState)
        const filter = rest.find((arg): arg is Filter<R> => typeof arg === 'function') ?? null
        const dependencies = rest.find((arg): arg is DependencyList => Array.isArray(arg)) ?? []

        const select = useCallback(selector, [manager, ...dependencies])

        const committedSelect = useRef(select)
        useLayoutEffect(() => {
            committedSelect.current = select
        })

        /**
         * Skips the filter in the render where `select` changed, so the new selection is applied rather than compared with the old one
         */
        const isEqual = useCallback(
            (previous: R, current: R) =>
                previous === current || (filter !== null && committedSelect.current === select && areResultsEqual(current, previous, filter)),
            [select]
        )

        return useStoreWithEqualityFn(manager.api, select, isEqual)
    }

    const useExtendedStateDispatcher: Dispatcher<S> = () => {
        const manager = useManager(useExtendedStateDispatcher)
        return useCallback(
            (newStateOrGenerator) =>
                manager.setState(typeof newStateOrGenerator === 'function' ? newStateOrGenerator(manager.getState()) : newStateOrGenerator),
            [manager]
        )
    }

    return { Provider, useExtendedState, useExtendedStateDispatcher }
}
